RivalIQ.module('graph', function (graph, App, Backbone, Marionette, $, _, d3) {

    'use strict';

    const pointMode = {
        ISOLATED: 1, // Draw points only when there are not two adjacent points
        ALL: 2 // Draw points at every data point
    };

    graph.TrendsLine = graph.Block.mixin({
        className: 'line',
        pointMode: pointMode.ALL,
        ticks: 3,

        mixins: {
            CalculatedMargin: graph.mixins.CalculatedMargin
        },

        margin: {
            top: 20,
            right: 10,
            bottom: 30
        },

        labelScale: function (len) {
            const me = this;

            return function (i) {
                if (len === 1) {
                    return _.last(me.x.range()) / 2;
                }

                return me.x(i);
            };
        },

        // Split line data into groups so we can support gaps when we have null
        // data
        dataToDataSegments: function (data) {
            return RivalIQ.utils.data.groupDataSegments(data, _.last);
        },

        _drawPoints: function (node, options) {
            const me = this;

            const dotGroup = node.selectAll('.dot-group')
                .data(function (d) {
                    if (me.pointMode === pointMode.ISOLATED) {
                        return _.map(d, function (group) {
                            return (group.length <= 1 ? group : []);
                        });
                    }

                    return d;
                });

            dotGroup
                .enter().append('g')
                .attr('class', 'dot-group');

            dotGroup
                .exit().remove();

            const dots = dotGroup.selectAll('.dot')
                .data(function (d) { return d; });

            dots
                .enter().append('circle')
                .attr('class', 'dot')
                .attr('stroke', 'rgb(31, 119, 180)')
                .attr('r', '1');

            dots
                .attr('cx', function (d) { return me.x(d[0]); })
                .attr('cy', function (d) { return me.y(d[d.length - 1]); });

            if (options.color) {
                dots.attr('fill', options.color);
            }

            dots
                .exit().remove();
        },

        _drawLineSegments: function (node, options) {
            const me = this;

            const line = d3.svg.line()
                .x(function (d) {
                    return me.x(d[0]);
                })
                .y(function (d) {
                    return me.y(d[d.length - 1]);
                });

            const path = node.selectAll('.line')
                .data(function (d) { return d; });

            path
                .enter().append('path')
                .attr('class', 'line');

            if (options.color) {
                path.attr('stroke', options.color);
            }

            _.forEach(options.additionalAttributes, function (attr, key) {
                path.attr(key, attr);
            });

            path
                .attr('d', line);

            path
                .exit().remove();
        },

        _drawLine: function (svg, data, metric, index, group, compare) {
            const me = this;
            const metricClassName = metric.getClassName();

            let classNames = ['line-group', `line${index}`, `group${group}`, metricClassName];
            if (me.getOption('showComparePeriod')) {
                if (compare) {
                    classNames.push('deemphasized');
                }
            }
            classNames = _.compact(classNames).join(' ');

            const options = {
                additionalAttributes: metric.get('additionalAttributes')
            };

            // fall back to color if no className
            if (!metricClassName) {
                options.color = function () {
                    return metric.getColor(data);
                };
            }

            data = me.dataToDataSegments(data);

            const lineGroup = svg.selectAll(`.line-group.line${index}.group${group}`)
                .data([data]);

            lineGroup.enter()
                .append('g');

            lineGroup
                .attr('class', classNames);

            me._drawLineSegments(lineGroup, options);
            me._drawPoints(lineGroup, options);
        },

        _drawTooltipPeriods: function (svg, data, domain) {
            const me = this;

            const dimensions = me.dimensions;
            const line = me._fillGaps(domain, _.head(data));

            const tooltipX = d3.scale.ordinal()
                .domain(d3.range(0, _.size(line) - 1))
                .rangeBands([0, dimensions.width], 0.0, 0);

            const tooltipY = d3.scale.linear()
                .domain([0, dimensions.height])
                .range([dimensions.height, 0]);

            const metric = me.getOption('metrics').first();
            const metricPeriod = metric.getPeriod();
            data = _.reduce(data, function (arr, line) {
                return arr.concat(_.map(line, function (column) {
                    const idx = _.head(column);
                    const periodDates = metric.getDataDate(idx);
                    const isPartialPeriod =
                        periodDates[0].isAfter(periodDates[0].clone().startOf(metricPeriod)) ||
                        periodDates[1].isBefore(periodDates[1].clone().endOf(metricPeriod));

                    const value = _.last(column);
                    return {
                        suppressTooltip: isPartialPeriod,
                        x: idx,
                        y0: 0,
                        y1: dimensions.height,
                        value: value
                    };
                }));
            }, []);

            data = _.values(_.groupBy(data, 'x'));
            data = _.map(data, function (column) {
                const values = _.map(column, 'value');
                return [_.extend(_.head(column), {
                    value: values
                })];
            });

            const period = svg.selectAll('.period')
                .data(data);

            period
                .enter()
                .append('g')
                .attr('class', 'period deemphasized transparent');

            period
                .attr('transform', function (d, i) {
                    // range points go edge to edge and since we are under-laying rangebands behind range points
                    // we need to cheat the bars back by 1/2 to get them centered. Unfortunately this results in
                    // "wrapping" on the last bar if we were to use the native tooltipX(i).
                    // Since we know that the domain is just the point count, we can cheat and do
                    // rangebandWidth * i so our last bar over flows the right edge by half instead of wrapping
                    let translateX = (tooltipX.rangeBand() * i) - (tooltipX.rangeBand() / 2);

                    if ((_.size(data) === 1 && !_.isFinite(translateX)) || i === 0) {
                        translateX = 0;
                    }

                    return `translate(${translateX},0)`;
                });

            period
                .exit()
                .remove();

            graph.utils.createTrendsTooltip(me, period, me.getOption('tooltip'));

            period
                .on('mouseenter.background', function (data) {
                    if (_.get(data, [0, 'suppressTooltip'])) {
                        return;
                    }

                    d3.select(this)
                        .classed('transparent', false);
                })
                .on('mouseleave.background', function () {
                    d3.select(this)
                        .classed('transparent', true);
                });


            const rect = period.selectAll('rect')
                .data(function (d) { return d; });

            rect
                .enter()
                .append('rect')
                .attr('fill', function () { return '#d3d3d3'; })
                .attr('height', function (d) { return Math.abs(tooltipY(d.y0) - tooltipY(d.y1)); });

            let width = tooltipX.rangeBand();
            const numDataPoints = _.size(data);

            // if we drew a line with just one tooltip, use the full width of the graph (rangeBand will be Inf since it'll be width / 0)
            if (numDataPoints === 1 && !_.isFinite(width)) {
                width = dimensions.width;
            }

            rect
                .attr('width', function (d, v, i) {
                    // the first & last tooltips should be half-width; otherwise, it can be far far
                    // away from the graph when we're on the left side of the page. See #16530
                    if (i === 0 || (numDataPoints > 1 && numDataPoints - 1 === i)) {
                        return width / 2;
                    }
                    return width;
                })
                .attr('y', function (d) { return tooltipY(d.y1); });

            rect
                .exit()
                .remove();

        },

        _fillGaps: function (domain, line) {
            _.each(_.range(domain[0], domain[1] + 1), function (value) {
                const found = _.find(line, function (vals) {
                    return parseInt(_.head(vals)) === value;
                });

                if (!found) {
                    line.push([value, null]);
                } else {
                    found[0] = parseInt(found[0]);
                }
            });

            return _.sortBy(line, _.head);
        },

        _getRange: function (rangeFnName) {
            const me = this;
            const metrics = me.getOption('metrics');
            let domain = metrics.invoke(rangeFnName);

            const domainMax = d3.max(_.map(domain, _.last));
            const domainMin = d3.min(_.map(domain, _.head));
            domain = [domainMin, domainMax];

            return domain;
        },

        _getDomainRange: function () {
            const me = this;
            return me._getRange('getDomainRange');
        },

        _getComparePeriodRange: function () {
            const me = this;
            return me._getRange('getComparePeriodDomainRange');
        },

        _getMainPeriodRange: function () {
            const me = this;
            return me._getRange('getMainPeriodDomainRange');
        },

        _getYDomain: function (data) {
            const me = this;

            data = (data || me.getOption('data'));

            let yMin;
            let yMax;
            _.each(data, function (datum) {
                _.each(datum, function (d) {
                    const value = _.last(d);

                    if (_.isFinite(value)) {
                        yMin = _.min([yMin, value]);
                        yMax = _.max([yMax, value]);
                    }

                });
            });

            return [yMin, yMax];
        },

        _getPaddedYDomain: function (data, fn) {
            const me = this;

            const domain = me._getYDomain(data);
            const metric = me.getOption('metrics').first();
            const paddedDomain = RivalIQ.graph.utils.getPaddedYDomain(metric, domain, me.yTicks, me.getOption('yMax'));

            const preferIntegerYTicks = me.getOption('preferIntegerYTicks');
            if (preferIntegerYTicks) {
                // eslint-disable-next-line prefer-const
                let [paddedYMin, paddedYMax] = paddedDomain;
                paddedYMax = Math.ceil(paddedYMax / me.yTicks) * me.yTicks;
                const range = paddedYMax - paddedYMin;
                if (Math.abs(range) < me.yTicks) {
                    paddedYMax = paddedYMin + me.yTicks;
                }
                paddedDomain[1] = paddedYMax;
            }
            return (!fn ? paddedDomain : fn(paddedDomain));
        },

        getYMax: function (data) {
            const me = this;

            const yMax = me.getOption('yMax');
            if (yMax) {
                // if the caller provided yMax, don't add in any padding.
                return yMax;
            }

            return me._getPaddedYDomain(data, _.last);
        },

        getYMin: function (data) {
            const me = this;

            const yMin = me.getOption('yMin');
            if (yMin) {
                // if the caller provided yMin, don't add in any padding.
                return yMin;
            }

            return me._getPaddedYDomain(data, _.head);
        },

        _drawDomain: function (svg, data) {
            const me = this;
            const metrics = me.getOption('metrics');

            me.y = d3.scale.linear()
                .domain(me._getPaddedYDomain(data))
                .range([me.dimensions.height, 0])
                .nice(me.yTicks);

            me._drawTicks(svg, data, metrics.first());
        },

        _drawLines: function (svg, data, domain) {
            const me = this;
            const metrics = me.getOption('metrics');

            _.each(data, function (line, i) {
                line = me._fillGaps(domain, line);
                me._drawLine(svg, line, metrics.at(i), i, 0);
            });
        },

        _drawLinesWithComparePeriod: function (svg, data, domain) {
            const me = this;
            const metrics = me.getOption('metrics');

            const comparePeriodRange = me._getComparePeriodRange();
            const mainPeriodRange = me._getMainPeriodRange();

            _.each(data, function (line, i) {
                // fill the gaps across the entire domain
                line = me._fillGaps(domain, line);

                let midpointY;
                let midpointX;
                const shiftEndpoints = Boolean(comparePeriodRange[1]);
                if (shiftEndpoints) {
                    const idx = _.last(comparePeriodRange);
                    midpointY = _.last(line[idx - 1]) + (_.last(line[idx]) - _.last(line[idx - 1])) / 2;
                    midpointX = idx - 0.5;
                }

                // split the line into compare period and main period
                _.each([comparePeriodRange, mainPeriodRange], function (range, j) {
                    const first = _.head(range);
                    const last = _.last(range);
                    const rangeLine = _.filter(line, function (point) {
                        return point[0] >= first && point[0] < last;
                    });

                    if (shiftEndpoints) {
                        if (j === 0) {
                            rangeLine.push([midpointX, rangeLine[0][1], midpointY]);
                        } else {
                            rangeLine.unshift([midpointX, rangeLine[0][1], midpointY]);
                        }
                    }

                    me._drawLine(svg, rangeLine, metrics.at(i), i, j, j === 0);
                });
            });
        },

        _drawAnnotations: function (svg, data) {
            const me = this;
            _.each(data, function (line) {
                me._drawAnnotationForLine(svg, line);
            });
        },

        _drawAnnotationForLine: function (svg, data) {
            const me = this;

            const annotationData = _.compact(_.map(me.getOption('annotations'), function (annotation) {
                const datum = _.find(data, function (datum) { return datum[0] === annotation.x; });
                return _.extend({
                    y: _.last(datum)
                }, annotation);
            }));

            const annotations = svg.selectAll('.annotation')
                .data(annotationData);

            annotations
                .attr('transform', function (d) {
                    return `translate(${me.x(d.x)}, ${me.y(d.y)})`;
                });

            annotations
                .enter()
                .append('g')
                .attr('class', 'annotation')
                .attr('transform', function (d) {
                    return `translate(${me.x(d.x)}, ${me.y(d.y)})`;
                })
                .append('text')
                .text(function (d) { return d.icon; })
                .attr('dx', '-0.5em')
                .attr('dy', '0.4em');

            annotations
                .exit()
                .remove();

            graph.utils.createTrendsTooltip(me, annotations, {
                content: function (d) { return d.information; }
            });
        },

        _getXScale: function (domain) {
            const me = this;
            if (domain[0] === domain[1]) {
                return function () { return me.dimensions.width / 2; };
            }

            return d3.scale.linear()
                .domain(domain)
                .range([0, me.dimensions.width]);
        },

        renderData: function (svg, data) {
            const me = this;
            // Note we subtract 1 because we are using a continuous range
            // but our domain is returned in the [start, end] format
            let domain = me._getDomainRange();
            domain = [domain[0], domain[1] - 1];

            me.x = me._getXScale(domain);

            me.y = d3.scale.linear()
                .range([me.dimensions.height, 0]);

            me._drawDomain(svg, data);

            if (me.getOption('showComparePeriod')) {
                me._drawLinesWithComparePeriod(svg, data, domain);
            } else {
                me._drawLines(svg, data, domain);
            }

            me._drawTooltipPeriods(svg, data, domain);
            me._drawAnnotations(svg, data);
        },

        onBeforeDestroy: function () {
            const me = this;

            me.$('.annotation').popover('destroy');
            me.$('.period').popover('destroy');
        }
    });

    graph.TrendsLine.reloadable = true;

}, window.d3);
